import { useCallback, useState } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { vendorService } from "@/services";
import { IGetVendorUsersResponse, IVendorUser } from "@/context/data.types";
import { initializeVendorUser } from "@/static/initial/VendorManagement";

/**
 * Custom hook for managing vendor users data
 * Follows the same pattern as useTask hook in the project
 */
const useVendorUsers = () => {
  const { setIsLoading } = useGlobalStateContext();
  const [vendorUsers, setVendorUsers] = useState<IVendorUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [vendorAssessment, setVendorAssessment] = useState<any>(null);

  /**
   * Fetch vendor users for a specific vendor
   * @param vendorId - The vendor ID to fetch users for
   * @param vendorAssessmentId - The vendor assessment ID to fetch users for (defaults to "0")
   * @returns Promise<IVendorUser[]> - Array of vendor users
   */
  const fetchVendorUsers = useCallback(async (vendorId: string, vendorAssessmentId: string = "0"): Promise<IVendorUser[]> => {
    if (!vendorId) {
      console.warn("Vendor ID is required to fetch vendor users");
      return [];
    }

    setIsLoadingUsers(true);
    
    try {
      const response: IGetVendorUsersResponse = await vendorService.getVendorUsers(vendorId, vendorAssessmentId || "0");
      
      // Capture vendorAssessment from API response
      if (response?.vendorAssessment) {
        setVendorAssessment(response.vendorAssessment);
      }
      
      if (response?.vendorUsers) {
        const formattedUsers = response.vendorUsers.map(initializeVendorUser);
        
        setVendorUsers(formattedUsers);
        return formattedUsers;
      } else {
        setVendorUsers([]);
        return [];
      }
    } catch (error: any) {
      console.error("Error fetching vendor users:", error);
      setVendorUsers([]);
      throw error;
    } finally {
      setIsLoadingUsers(false);
    }
  }, []); // Remove setIsLoading dependency

  /**
   * Fetch vendor users with global loading state
   * @param vendorId - The vendor ID to fetch users for
   * @param vendorAssessmentId - The vendor assessment ID to fetch users for (defaults to "0")
   * @returns Promise<IVendorUser[]> - Array of vendor users
   */
  const fetchVendorUsersWithGlobalLoading = useCallback(async (vendorId: string, vendorAssessmentId: string = "0"): Promise<IVendorUser[]> => {
    if (!vendorId) {
      console.warn("Vendor ID is required to fetch vendor users");
      return [];
    }

    setIsLoading(true);
    setIsLoadingUsers(true);
    
    try {
      const response: IGetVendorUsersResponse = await vendorService.getVendorUsers(vendorId, vendorAssessmentId || "0");
      
      // Capture vendorAssessment from API response
      if (response?.vendorAssessment) {
        setVendorAssessment(response?.vendorAssessment);
      }
      
      if (response?.vendorUsers) {
        const formattedUsers = response.vendorUsers.map(initializeVendorUser);
        setVendorUsers(formattedUsers);
        return formattedUsers;
      } else {
        setVendorUsers([]);
        return [];
      }
    } catch (error: any) {
      console.error("Error fetching vendor users:", error);
      setVendorUsers([]);
      throw error;
    } finally {
      setIsLoading(false);
      setIsLoadingUsers(false);
    }
  }, [setIsLoading]);

  /**
   * Refresh vendor users data
   * @param vendorId - The vendor ID to refresh users for
   * @param vendorAssessmentId - The vendor assessment ID to refresh users for (defaults to "0")
   * @returns Promise<IVendorUser[]> - Array of vendor users
   */
  const refreshVendorUsers = useCallback(async (vendorId: string, vendorAssessmentId: string = "0"): Promise<IVendorUser[]> => {
    return fetchVendorUsers(vendorId, vendorAssessmentId);
  }, [fetchVendorUsers]);

  /**
   * Clear vendor users data
   */
  const clearVendorUsers = useCallback(() => {
    setVendorUsers([]);
    setVendorAssessment(null);
  }, []);

  /**
   * Add a new vendor user to the list
   * @param user - The vendor user to add
   */
  const addVendorUser = useCallback((user: IVendorUser) => {
    setVendorUsers(prev => [...prev, user]);
  }, []);

  /**
   * Remove a vendor user from the list
   * @param index - The index of the user to remove
   */
  const removeVendorUser = useCallback((index: number) => {
    setVendorUsers(prev => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Update a vendor user in the list
   * @param index - The index of the user to update
   * @param user - The updated user data
   */
  const updateVendorUser = useCallback((index: number, user: IVendorUser) => {
    setVendorUsers(prev => prev.map((u, i) => i === index ? user : u));
  }, []);

  /**
   * Replace all vendor users with new data
   * @param users - The new array of vendor users
   */
  const setVendorUsersData = useCallback((users: IVendorUser[]) => {
    setVendorUsers(users);
  }, []);

  return {
    vendorUsers,
    vendorAssessment,
    isLoadingUsers,
    fetchVendorUsers,
    fetchVendorUsersWithGlobalLoading,
    refreshVendorUsers,
    clearVendorUsers,
    addVendorUser,
    removeVendorUser,
    updateVendorUser,
    setVendorUsersData
  };
};

export default useVendorUsers;

// import { useEffect, useState } from "react";
// import vendorUsersService, { VendorUser } from "@/services/vendorUsersService";

// type UseVendorUsersResult = {
//   users: VendorUser[];
//   loading: boolean;
//   error: any;
//   reload: () => Promise<void>;
// };

// export function useVendorUsers(vendorId: string = "135467"): UseVendorUsersResult {
//   const [users, setUsers] = useState<VendorUser[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [error, setError] = useState<any>(null);

//   const load = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const data = await vendorUsersService.list(vendorId);
//     //   const sanitized = Array.isArray(data)
//     //     ? data.map(item => ({
//     //         ...item,
//     //         modifiedOn:
//     //           typeof item.modifiedOn === "string"
//     //             ? (item.modifiedOn.split(" ")[0] || item.modifiedOn)
//     //             : item.modifiedOn,
//     //       }))
//     //     : [];
//       setUsers(data);
//     } catch (err) {
//       setError(err);
//       setUsers([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     load();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [vendorId]);

//   return { users, loading, error, reload: load };
// }

// export default useVendorUsers;


